@for (widget of widgets(); track widget.type) {
  <div class="widget-card" [class.widget-card--bank-details]="widget.type === 'bank_details'">
    <div class="widget-card__header">
      {{ getTitle(widget.type, widget.data) }}
    </div>
    <div class="widget-card__body">

      <!-- Claim Overview -->
      @if (widget.type === 'claim_overview') {
        <div class="widget-card__claim-header">
          <span class="widget-card__claim-ref">{{ widget.data['aktenzeichen'] }}</span>
          <span class="widget-card__claim-creditor">{{ widget.data['glaeubiger'] }}</span>
        </div>

        <div class="widget-card__line-items">
          <div class="widget-card__line-item">
            <span class="widget-card__line-label">
              @if (widget.data['primary_claim_label_key']) {
                {{ widget.data['primary_claim_label_key'] | t }}
              } @else {
                {{ 'widget.openMainClaim' | t }}
              }
            </span>
            <span class="widget-card__line-amount">{{ widget.data['open_hauptforderung'] ?? widget.data['hauptforderung'] }}</span>
          </div>
          <div class="widget-card__line-item">
            <span class="widget-card__line-label">{{ 'widget.openInterest' | t }}</span>
            <span class="widget-card__line-amount">{{ widget.data['open_zinsen'] ?? widget.data['zinsen'] }}</span>
          </div>
          <div class="widget-card__line-item">
            <span class="widget-card__line-label">{{ 'widget.openCosts' | t }}</span>
            <span class="widget-card__line-amount">{{ widget.data['open_kosten'] ?? widget.data['kosten'] }}</span>
          </div>
        </div>

        <div class="widget-card__total">
          <span class="widget-card__total-label">{{ 'widget.openTotal' | t }}</span>
          <span class="widget-card__total-amount">{{ widget.data['open_total_amount'] ?? widget.data['gesamtforderung'] }}</span>
        </div>

        <div class="widget-card__supplementary">
          <span class="widget-card__supplementary-label">{{ 'widget.alreadyPaid' | t }}</span>
          <span class="widget-card__supplementary-amount">{{ widget.data['already_paid_booked'] ?? widget.data['zahlungen'] }}</span>
        </div>

        @if (claimDetailsAvailable() && hasClaimDetails(widget.data)) {
          <button type="button" class="widget-card__copy-btn" aria-haspopup="dialog"
            (click)="claimDetails.emit($any($event.currentTarget))">
            {{ 'claim.showDetails' | t }}
          </button>
        }

      }

      <!-- Bank Details -->
      @if (widget.type === 'bank_details') {
        <div class="widget-card__iban-row">
          <span class="widget-card__section-label">{{ 'caseView.detailLabels.bank_iban' | t }}</span>
          <span class="widget-card__iban">{{ widget.data['iban'] }}</span>
          <button
            class="widget-card__copy-btn"
            type="button"
            (click)="copyIban(widget.data['iban'])"
            [attr.aria-label]="'widget.copyIban' | t"
            [title]="'widget.copyIban' | t">
            {{ 'common.copy' | t }}
          </button>
        </div>
        <div class="widget-card__meta">
          <span class="widget-card__label">BIC:</span>
          <span class="widget-card__value">{{ widget.data['bic'] }}</span>
        </div>
        <div class="widget-card__meta">
          <span class="widget-card__label">Bank:</span>
          <span class="widget-card__value">{{ widget.data['bank'] }}</span>
        </div>
        <div class="widget-card__meta">
          <span class="widget-card__label">{{ 'widget.recipient' | t }}</span>
          <span class="widget-card__value">{{ widget.data['empfaenger'] }}</span>
        </div>
        <div class="widget-card__meta widget-card__meta--reference">
          <span class="widget-card__label">{{ 'widget.reference' | t }}</span>
          <span class="widget-card__value widget-card__value--bold">{{ widget.data['verwendungszweck'] }}</span>
        </div>
        @if (transferQrCode(); as qrCode) {
          <div class="widget-card__qr">
            <img [src]="qrCode" alt="GiroCode" width="210" height="210">
            <span>{{ 'quickActionWizard.giroCodeInfo' | t }}</span>
          </div>
        }
      }

      <!-- Payment Terms -->
      @if (widget.type === 'payment_terms') {
        @if (widget.data['countOptions']; as options) {
          <div class="widget-card__choices">
            @for (option of options; track option.payload) {
              @if (actionFor(option.payload); as action) {
              <button type="button" class="widget-card__choice"
                [disabled]="disabled() || !option.available"
                (click)="actionClick.emit(action)">
                <strong>{{ action.label }}</strong>
                @if (option.count && option.available) {
                  <span>{{ 'widget.choiceMonthlyRate' | t }}: {{ option.amount }}</span>
                  <span>{{ 'widget.choiceTotal' | t }}: {{ option.total }}</span>
                  @if (option.fee_free) {
                    <span>{{ 'widget.noAdditionalAgreementFee' | t }}</span>
                  } @else {
                    <span>{{ 'widget.additionalAgreementFee' | t }}: {{ option.fee }}</span>
                  }
                } @else if (!option.available) {
                  <span>{{ 'widget.notAvailable' | t }}</span>
                }
              </button>
              }
            }
          </div>
          <p class="widget-card__installment-note">{{ 'widget.ongoingInterestNotice' | t }}</p>
        } @else {
        <div class="widget-card__line-items">
          <div class="widget-card__line-item">
            <span class="widget-card__line-label">{{ 'widget.installment' | t }}</span>
            <span class="widget-card__line-amount">{{ widget.data['rate6MonthsWithFee'] ?? widget.data['rate6Months'] ?? widget.data['sechsMonatsRate'] ?? widget.data['empfohleneRate'] ?? widget.data['mindestrate'] }}</span>
          </div>
	      <div class="widget-card__line-item">
	        <span class="widget-card__line-label">{{ 'widget.maxDuration' | t }}</span>
	        <span class="widget-card__line-amount">{{ widget.data['maxDurationMonths'] }} {{ 'widget.months' | t }}</span>
	      </div>
          <div class="widget-card__line-item">
            <span class="widget-card__line-label">{{ 'widget.includedInstallmentFee' | t }}</span>
            <span class="widget-card__line-amount">{{ widget.data['rzvFeeHonorar'] }}</span>
          </div>
          <div class="widget-card__line-item">
            <span class="widget-card__line-label">{{ 'widget.includedExpenseFlatFee' | t }}</span>
            <span class="widget-card__line-amount">{{ widget.data['rzvFeeAuslagen'] }}</span>
          </div>
          <div class="widget-card__line-item">
            <span class="widget-card__line-label">{{ 'widget.rhythm' | t }}</span>
            <span class="widget-card__line-amount">{{ widget.data['rhythmus'] }}</span>
          </div>
        </div>
        }
      }

      <!-- Payment History -->
      @if (widget.type === 'payment_history') {
        @if (widget.data['rateninformationen']) {
          <div class="widget-card__section-label">
            {{ 'widget.titles.payment_history' | t }}
          </div>
        }

        @if (widget.data['zahlungen'] && widget.data['zahlungen'].length > 0) {
          <div class="widget-card__history-list">
            @for (zahlung of widget.data['zahlungen']; track zahlung['datum']) {
              <div class="widget-card__history-item">
                <span class="widget-card__history-date">
                  {{ formatDate(zahlung['datum']) }}
                  @if (zahlung['bezeichnung']) {
                    <small>{{ zahlung['bezeichnung'] }}</small>
                  }
                </span>
                <span class="widget-card__history-amount">{{ zahlung['betrag'] }}</span>
              </div>
            }
          </div>
        } @else {
          <div class="widget-card__empty">{{ 'widget.noPayments' | t }}</div>
        }

        @if (widget.data['rateninformationen']; as rateInfo) {
          <div class="widget-card__section-label widget-card__section-label--spaced">
            {{ 'widget.nextPayments' | t }}
          </div>

          <div class="widget-card__rate-info">
            @if (rateInfo['anzahlRaten']) {
              <div class="widget-card__line-item">
                <span class="widget-card__line-label">{{ 'widget.installments' | t }}</span>
                <span class="widget-card__line-amount">{{ rateInfo['anzahlRaten'] }}</span>
              </div>
            }
            @if (rateInfo['zahlungsart']) {
              <div class="widget-card__line-item">
                <span class="widget-card__line-label">{{ 'widget.paymentType' | t }}</span>
                <span class="widget-card__line-amount">{{ rateInfo['zahlungsart'] }}</span>
              </div>
            }

            @for (rate of [
              { label: 'widget.rateCurrent', value: rateInfo['aktuelleRate'] },
              { label: 'widget.rateNext', value: rateInfo['naechsteRate'] },
              { label: 'widget.rateFollowing', value: rateInfo['uebernaechsteRate'] },
              { label: 'widget.ratePenultimate', value: rateInfo['vorletzteRate'] },
              { label: 'widget.rateLast', value: rateInfo['letzteRate'] }
            ]; track rate.label) {
              @if (rate.value && (rate.label !== 'widget.rateLast' || !hasDuplicateLastRate(rateInfo))) {
                <div class="widget-card__rate-row">
                  <span class="widget-card__rate-label">{{ rate.label | t }}</span>
                  <span class="widget-card__rate-value">
                    @if (rate.value['datum']) {
                      <span>{{ formatDate(rate.value['datum']) }}</span>
                    }
                    @if (rate.value['betrag']) {
                      <strong>{{ rate.value['betrag'] }}</strong>
                    }
                  </span>
                </div>
              }
            }
          </div>
        }
      }

      <!-- Payment Agreement -->
      @if (widget.type === 'payment_agreement') {
        <div class="widget-card__line-items">
          @if (widget.data['ratenplan']; as schedule) {
            @for (installment of schedule; track installment['index']) {
              <div class="widget-card__line-item">
                <span class="widget-card__line-label">
                  {{ 'widget.installment' | t }} {{ installment['index'] }} · {{ formatDate(installment['due_date']) }}
                  @if (installment['has_deviating_final_installment_note'] === true) {
                    <span class="widget-card__installment-note">{{ 'p1.finalInstallmentNote' | t }}</span>
                  }
                </span>
                <span class="widget-card__line-amount">{{ installment['amount'] }}</span>
              </div>
            }
          } @else {
            <div class="widget-card__line-item">
              <span class="widget-card__line-label">{{ 'widget.monthlyRate' | t }}</span>
              <span class="widget-card__line-amount">{{ widget.data['rate'] }}</span>
            </div>
          }
          <div class="widget-card__line-item">
            <span class="widget-card__line-label">{{ 'widget.rhythm' | t }}</span>
            <span class="widget-card__line-amount">{{ widget.data['rhythmus'] }}</span>
          </div>
          <div class="widget-card__line-item">
            <span class="widget-card__line-label">{{ 'widget.start' | t }}</span>
            <span class="widget-card__line-amount">{{ formatDate(widget.data['start']) }}</span>
          </div>
          <div class="widget-card__line-item">
            <span class="widget-card__line-label">{{ 'widget.duration' | t }}</span>
            <span class="widget-card__line-amount">{{ widget.data['laufzeit'] }}</span>
          </div>
        </div>
      }

      <!-- Creditreform Contact -->
      @if (widget.type === 'creditreform_contact') {
        <div class="widget-card__contact">
          <div class="widget-card__contact-name">{{ widget.data['name'] }}</div>

          @if (widget.data['unavailable']) {
            <div class="widget-card__empty">{{ widget.data['hinweis'] }}</div>
          } @else {
            <div class="widget-card__contact-list">
              @if (widget.data['phone']) {
                <a data-track="contact_phone" class="widget-card__contact-row" [attr.href]="'tel:' + widget.data['phone']">
                  <span class="widget-card__contact-label">{{ 'widget.phone' | t }}</span>
                  <span class="widget-card__contact-value">{{ widget.data['phone'] }}</span>
                </a>
              }
              @if (widget.data['whatsapp']) {
                <div class="widget-card__contact-row">
                  <span class="widget-card__contact-label">{{ 'widget.whatsapp' | t }}</span>
                  <span class="widget-card__contact-value">{{ widget.data['whatsapp'] }}</span>
                </div>
              }
              @if (widget.data['email']) {
                <a data-track="contact_email" class="widget-card__contact-row" [attr.href]="'mailto:' + widget.data['email']">
                  <span class="widget-card__contact-label">{{ 'widget.email' | t }}</span>
                  <span class="widget-card__contact-value">{{ widget.data['email'] }}</span>
                </a>
              }
              @if (widget.data['address']) {
                <div class="widget-card__contact-row">
                  <span class="widget-card__contact-label">{{ 'widget.postalAddress' | t }}</span>
                  <span class="widget-card__contact-value">{{ widget.data['address'] }}</span>
                </div>
              }
              @if (widget.data['website_url']) {
                <a data-track="contact_website" class="widget-card__contact-row" [attr.href]="widget.data['website_url']" target="_blank" rel="noopener">
                  <span class="widget-card__contact-label">{{ 'widget.webContact' | t }}</span>
                  <span class="widget-card__contact-value">{{ widget.data['website_label'] }}</span>
                </a>
              }
            </div>

            @if (widget.data['servicezeiten'] && widget.data['servicezeiten'].length > 0) {
              <div class="widget-card__service-hours">
                <span class="widget-card__section-label">{{ 'widget.serviceHours' | t }}</span>
                @for (line of widget.data['servicezeiten']; track line) {
                  <span class="widget-card__service-line">{{ line }}</span>
                }
              </div>
            }
          }
        </div>
      }

    </div>
  </div>
}
